/* CLIKEA GAMING cart: quick add from cards (stays on the page) and the full cart
   page (quantities, coupons, CLIKEA Protect, note, share). The cart page section
   is re-rendered with the Section Rendering API after every change. */
(() => {
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const post = async (url, body) => {
    const response = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'No pude actualizar tu bolsa.');
    return data;
  };

  const innerOf = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.shopify-section')?.innerHTML ?? html;
  };

  async function refresh(sectionIds) {
    const ids = ['cart-icon-bubble', ...sectionIds];
    // No Accept: application/json here: on /cart that header returns the cart object instead of the sections.
    const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const sections = await response.json();
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = innerOf(sections['cart-icon-bubble']);
    sectionIds.forEach((id) => {
      const holder = document.getElementById(`shopify-section-${id}`);
      if (holder && sections[id]) holder.innerHTML = innerOf(sections[id]);
    });
  }

  // ---------- quick add from product cards ----------
  let toastTimer = null;
  const toast = (text) => {
    let node = document.querySelector('.clg-toast');
    if (!node) {
      node = document.createElement('div');
      node.className = 'clg-toast';
      node.setAttribute('role', 'status');
      document.body.appendChild(node);
    }
    node.innerHTML = `<span>${text}</span><a href="${window.routes?.cart_url || '/cart'}">Ver bolsa</a>`;
    node.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => node.classList.remove('is-visible'), 3500);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.clg-add-form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (!button || button.classList.contains('loading')) return;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    try {
      await post('/cart/add.js', {
        items: [{ id: Number(form.querySelector('[name="id"]').value), quantity: Number(form.querySelector('[name="quantity"]')?.value) || 1 }],
      });
      await refresh([]);
      button.classList.add('is-added');
      const label = button.querySelector('.clg-card__add-label');
      const original = label?.textContent;
      if (label) label.textContent = '✓ Agregado';
      setTimeout(() => {
        button.classList.remove('is-added');
        if (label && original) label.textContent = original;
      }, 2500);
      toast('Agregado a tu bolsa');
    } catch (error) {
      toast(error.message);
    } finally {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  });

  // ---------- full cart page ----------
  const cartRoot = () => document.querySelector('[data-clg-cart]');
  const sectionId = () => cartRoot()?.dataset.sectionId;
  const message = (text) => {
    const node = document.querySelector('[data-coupon-msg]');
    if (node) node.textContent = text;
  };

  async function run(task, after) {
    const root = cartRoot();
    if (!root || root.classList.contains('is-busy')) return;
    root.classList.add('is-busy');
    try {
      const result = await task();
      await refresh([sectionId()]);
      if (after) after(result);
    } catch (error) {
      await refresh([sectionId()]).catch(() => undefined);
      message(error.message);
    } finally {
      cartRoot()?.classList.remove('is-busy');
    }
  }

  const setDiscount = (code) => post('/cart/update.js', { discount: code });
  const readCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());
  // One CLIKEA Protect line per PC (property "Equipo protegido"), always covering every unit of that PC.
  const planLines = (cart, title) =>
    (cart.items || []).filter((item) => item.properties && item.properties['Equipo protegido'] === title);
  const pcUnits = (cart, title) =>
    (cart.items || [])
      .filter((item) => item.product_title === title && !(item.properties && item.properties['Equipo protegido']))
      .reduce((total, item) => total + item.quantity, 0);
  async function syncPlans(cart, title) {
    const units = pcUnits(cart, title);
    const [first, ...extra] = planLines(cart, title);
    for (const line of extra) await post('/cart/change.js', { id: line.key, quantity: 0 });
    if (first && first.quantity !== units) await post('/cart/change.js', { id: first.key, quantity: units });
  }

  document.addEventListener('click', (event) => {
    if (!cartRoot()) return;
    const qty = event.target.closest('[data-cart-qty]');
    if (qty) {
      const row = qty.closest('[data-line]');
      const line = Number(row?.dataset.line);
      const title = row?.dataset.pcTitle;
      run(async () => {
        const cart = await post('/cart/change.js', { line, quantity: Math.max(0, Number(qty.dataset.cartQty)) });
        if (title) await syncPlans(cart, title);
      });
      return;
    }
    const apply = event.target.closest('[data-coupon-apply]');
    if (apply) {
      const code = apply.dataset.couponApply;
      run(() => setDiscount(code), (state) => {
        const found = (state.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
        message(found?.applicable ? '¡Cupón aplicado!' : 'Ese cupón no aplica a esta compra.');
      });
      return;
    }
    if (event.target.closest('[data-coupon-remove]')) {
      run(() => setDiscount(''), () => message('Cupón quitado.'));
      return;
    }
    const years = event.target.closest('[data-cart-years]');
    if (years) {
      const box = years.closest('[data-protect-box]');
      box.querySelectorAll('[data-cart-years]').forEach((button) => button.setAttribute('aria-pressed', String(button === years)));
      box.querySelectorAll('[data-protect-years]').forEach((plan) => {
        plan.hidden = plan.dataset.protectYears !== years.dataset.cartYears;
      });
      return;
    }
    const toggle = event.target.closest('[data-protect-toggle]');
    if (toggle) {
      const picker = toggle.closest('[data-protect-box]')?.querySelector('[data-protect-picker]');
      if (picker) picker.hidden = !picker.hidden;
      return;
    }
    const setPlan = event.target.closest('[data-protect-set]');
    if (setPlan) {
      const box = setPlan.closest('[data-protect-box]');
      const title = box.dataset.pcTitle;
      run(async () => {
        // Replace the plan this PC already has instead of adding a second one.
        const cart = await readCart();
        for (const line of planLines(cart, title)) await post('/cart/change.js', { id: line.key, quantity: 0 });
        await post('/cart/add.js', {
          items: [{ id: Number(setPlan.dataset.protectSet), quantity: pcUnits(cart, title) || 1, properties: { 'Equipo protegido': title } }],
        });
      }, () => message('CLIKEA Protect actualizado.'));
      return;
    }
    const removePlan = event.target.closest('[data-protect-remove]');
    if (removePlan) {
      run(() => post('/cart/change.js', { id: removePlan.dataset.protectRemove, quantity: 0 }), () => message('Protección quitada.'));
      return;
    }
    if (event.target.closest('[data-cart-share]')) {
      const status = document.querySelector('[data-cart-share-msg]');
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then(async (cart) => {
          const items = (cart.items || []).map((item) => `${item.variant_id}:${item.quantity}`).join(',');
          const url = `${window.location.origin}/cart/${items}`;
          if (navigator.share) await navigator.share({ title: 'Mi bolsa en CLIKEA GAMING', url });
          else {
            await navigator.clipboard.writeText(url);
            if (status) status.textContent = 'Enlace copiado.';
          }
        })
        .catch(() => {
          if (status) status.textContent = 'No pude compartir la bolsa.';
        });
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-coupon-form]');
    if (!form || !cartRoot()) return;
    event.preventDefault();
    const code = form.querySelector('input')?.value.trim();
    if (!code) return;
    run(() => setDiscount(code), (state) => {
      const found = (state.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
      message(found?.applicable ? '¡Cupón aplicado!' : 'Ese cupón no aplica a esta compra.');
    });
  });

  let noteTimer = null;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => post('/cart/update.js', { note: note.value }).catch(() => undefined), 600);
  });
})();
